/**
 * Runtime (as opposed to build-time) Firebase config resolution.
 *
 * `environment.ts` / `environment.prod.ts` each bake in a default
 * `firebaseConfig` at `ng build` time via Angular's `fileReplacements`.
 * That is sufficient for local development and for Firebase Hosting
 * deploys, where a given deploy is only ever meant to talk to one
 * Firebase project.
 *
 * The Docker image built from this repo needs to do better than that: one
 * built image should be able to point at either the dev or production
 * Firebase project (or a fully custom one) depending on how it's *run*,
 * not how it was built. To make that possible, `docker/docker-entrypoint.d/`
 * writes the real config into `assets/env.js` (see that script) based on the
 * `FIREBASE_ENV` environment variable passed to `docker run`, which sets
 * `window.__env` before Angular's own bundles execute. If that value is
 * present and looks complete, it overrides the build-time default below;
 * otherwise the build-time default is used unchanged.
 */

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface RuntimeEnv {
  firebaseConfig?: Partial<FirebaseWebConfig>;
  knightmedsInstanceId?: string;
}

declare global {
  interface Window {
    __env?: RuntimeEnv;
  }
}

export function resolveFirebaseConfig(buildTimeConfig: FirebaseWebConfig): FirebaseWebConfig {
  const runtimeConfig = window.__env?.firebaseConfig;

  // Only trust the runtime config once it's been filled in -- the checked-in
  // assets/env.js stub ships with empty placeholders so non-Docker builds
  // (ng serve, plain `ng build` + Firebase Hosting) fall through to the
  // build-time default unchanged.
  if (runtimeConfig?.apiKey && runtimeConfig?.projectId) {
    return {
      ...buildTimeConfig,
      ...runtimeConfig,
    };
  }

  return buildTimeConfig;
}

/**
 * Knightmeds cabinet ("instance") id resolution.
 *
 * A cabinet id can reach the app three ways, checked in this order:
 *
 *   1. `?cabinet=<id>` on the URL the laptop was opened with. This is the
 *      provisioning path for hosted (Firebase Hosting) deployments: you open
 *      the URL once when setting the laptop up, and it sticks from then on.
 *      An id from the URL is NOT trusted on sight -- it is a *pending*
 *      candidate until knightmedsInstanceGuard confirms a matching
 *      knightmedsInstances/{id} document exists, and only then is it
 *      committed (see commitKnightmedsInstanceId). That's what keeps a typo
 *      from silently creating a phantom cabinet whose inventory nobody sees.
 *   2. localStorage, written by that commit. Per-origin and per-laptop, so
 *      it survives refreshes, restarts, and router navigations that drop the
 *      query string -- which is the whole point of persisting it rather than
 *      requiring the param every time.
 *   3. `window.__env.knightmedsInstanceId`, injected by the Docker image from
 *      the KNIGHTMEDS_INSTANCE_ID environment variable (see
 *      docker/docker-entrypoint.d/40-inject-env.sh). Unchanged and still
 *      supported -- existing per-laptop Docker installs need no migration.
 *
 * The URL param is captured once at startup, before Angular bootstraps, by
 * captureKnightmedsInstanceIdFromUrl() in main.ts. It cannot be read later
 * from the router: the post-login redirect to the release pages drops the
 * query string, so by the time any guard or component runs, `?cabinet=` is
 * already gone from the URL.
 */
const CABINET_QUERY_PARAM = 'cabinet';
const CABINET_STORAGE_KEY = 'knightmedsInstanceId';

/**
 * The id read from `?cabinet=` at startup, still awaiting validation against
 * Firestore. Cleared once committed.
 */
let pendingInstanceId: string | undefined;

/**
 * Whether the URL has been read yet. Capture is lazy and idempotent rather
 * than a single call at a fixed point in startup, because ES imports are
 * hoisted: every module main.ts imports -- app.routes.ts among them -- is
 * fully evaluated before main.ts's own body runs. An imperative "capture
 * first" call in main.ts therefore cannot be relied on to beat a module that
 * reads the id while being imported. Capturing on first read instead makes
 * the order irrelevant; any reader before the router's first redirect sees
 * the query string, and every reader after gets the same memoized answer.
 */
let hasCapturedFromUrl = false;

/**
 * A validated id for this page load, kept in memory as well as in
 * localStorage so the session still works when site data is unwritable
 * (private windows, browsers configured to block storage).
 */
let committedInstanceId: string | undefined;

/**
 * Reads `?cabinet=<id>` off the URL the app was opened with, once. Safe to
 * call at any point: the first call captures, later ones are no-ops, so the
 * answer never changes even after the router has dropped the query string.
 */
export function captureKnightmedsInstanceIdFromUrl(): void {
  if (hasCapturedFromUrl) {
    return;
  }
  hasCapturedFromUrl = true;
  const fromUrl = new URLSearchParams(window.location.search).get(CABINET_QUERY_PARAM);
  pendingInstanceId = fromUrl?.trim() || undefined;
}

/**
 * Returns the not-yet-validated id captured from the URL, if there was one.
 * Only knightmedsInstanceGuard (and the error page it redirects to) should
 * need this -- everywhere else wants a validated id.
 */
export function getPendingKnightmedsInstanceId(): string | undefined {
  captureKnightmedsInstanceIdFromUrl();
  return pendingInstanceId;
}

/**
 * Whether this browser has a cabinet id at all -- from `?cabinet=`, from a
 * previous visit's localStorage, or from the Docker environment variable.
 *
 * This is what decides whether the app runs as a medication cabinet or as
 * the ordinary EMR, so it deliberately does NOT care whether the id is
 * valid: an id that turns out to be unknown still means "this was opened as
 * a cabinet", and should get knightmedsInstanceGuard's specific error rather
 * than being quietly treated as a desktop. See isKnightmedsCabinetMode().
 */
export function hasKnightmedsCabinetId(): boolean {
  return !!(getPendingKnightmedsInstanceId() || tryResolveKnightmedsInstanceId());
}

/**
 * Accepts an id that has been confirmed to exist in Firestore, persisting it
 * as this laptop's cabinet. Deliberately called only after validation: a
 * typo'd id written to localStorage would be baked into the machine, and
 * clearing it means talking someone through wiping site data.
 */
export function commitKnightmedsInstanceId(instanceId: string): void {
  committedInstanceId = instanceId;
  pendingInstanceId = undefined;
  try {
    localStorage.setItem(CABINET_STORAGE_KEY, instanceId);
  } catch {
    // Best effort. committedInstanceId still carries it for this page load;
    // the laptop just has to be re-provisioned via ?cabinet= next time.
  }
}

function readStoredInstanceId(): string | undefined {
  try {
    return localStorage.getItem(CABINET_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns this app instance's validated Knightmeds cabinet id, or undefined
 * if none is set. Never throws -- use this instead of
 * resolveKnightmedsInstanceId() anywhere a missing instance id shouldn't be
 * fatal, e.g. a UI label that can fall back to a generic name.
 *
 * Note this ignores any pending `?cabinet=` id: until the guard has checked
 * it against Firestore it is only a claim, not this laptop's identity.
 */
export function tryResolveKnightmedsInstanceId(): string | undefined {
  return committedInstanceId
    || readStoredInstanceId()
    || window.__env?.knightmedsInstanceId
    || undefined;
}

/**
 * Resolves the id of the physical Knightmeds cabinet this app instance is
 * running as. Used to scope Firestore reads/writes under
 * knightmedsInstances/{instanceId}/... so multiple physical cabinets can
 * share one Firebase project without colliding.
 *
 * Deliberately NOT called at module load / app bootstrap: most deployments
 * (plain ng serve, Firebase Hosting, or Docker images with knightMedsInstall
 * disabled) never touch Knightmeds Firestore paths at all and must not be
 * broken by a missing instance id. Callers must invoke this lazily, only
 * when Knightmeds Firestore access actually happens (see ContainersService).
 *
 * Reaching the throw below should be impossible from inside the Knightmeds
 * pages, which knightmedsInstanceGuard already redirects away when no valid
 * cabinet is configured. It stays as a backstop for any future caller that
 * isn't behind that guard.
 */
export function resolveKnightmedsInstanceId(): string {
  const instanceId = tryResolveKnightmedsInstanceId();
  if (!instanceId) {
    throw new Error(
      'No Knightmeds cabinet id is configured. Each physical cabinet must ' +
      'have a unique id so its data is stored under its own ' +
      'knightmedsInstances/{instanceId}/containers path in Firestore -- ' +
      'without this, cabinets sharing a Firebase project would overwrite ' +
      "each other's inventory data. Provision this laptop by opening the " +
      'app once with ?cabinet=<id> (the id must already exist in the ' +
      'knightmedsInstances collection), or set the KNIGHTMEDS_INSTANCE_ID ' +
      'environment variable if running the Docker image (see ' +
      'docker/docker-entrypoint.d/40-inject-env.sh).'
    );
  }
  return instanceId;
}
